/**
 * The one answer control an administrator gets for a Watch event (B2, B3), shared by the event page and the Activity
 * list so both always choose the same route:
 *   'own'       the admin received the notice: their own question (the page the email link opens);
 *   'stand-in'  nobody was notified and the portal offers the admin's own answer (Expected / Not expected,
 *               POST .../attestation with asAdmin);
 *   'override'  otherwise: Mark as expected (POST .../mark-expected) or Flag as unexpected (POST .../cases).
 * `data` is the event detail response (GET /customers/{id}/watch/events/{eventId}).
 */
export function adminAnswerMode(data) {
  if (data?.myNotification) return 'own';
  if (data?.adminAnswer && (data.recipients ?? []).length === 0) return 'stand-in';
  return 'override';
}

const failure = (message) => Object.assign(new Error(message), { refused: true });

/**
 * Answers an event as an administrator from the Activity list: reads the event, picks the control the event page would
 * show and calls the same API. Resolves to the fields that changed on the event; rejects with a readable message when
 * the portal (or the event's state) refuses the answer.
 */
export async function answerAsAdmin(api, customerId, eventId, answer) {
  const d = await api.getWatchEvent(customerId, eventId);
  if (d?.event?.noticeClass === 'admin-notice') throw failure('This is a record of a change to Watch itself. It has no question to answer.');
  if (d?.case || d?.event?.caseId) throw failure('This event is already flagged for follow-up.');
  if (adminAnswerMode(d) === 'stand-in') {
    const a = d.adminAnswer;
    if (!a.canAnswer) throw failure(a.reason || 'This event can no longer be answered here.');
    await api.attestWatchNotification(customerId, a.notificationId, { answer, asAdmin: true });
    return { answerState: answer };
  }
  if (answer === 'yes') {
    const mark = d?.markExpected;
    if (!mark?.canMark) throw failure(mark?.reason ? `You cannot mark this event as expected: ${mark.reason}` : 'This event can no longer be marked as expected.');
    const r = await api.markWatchEventExpected(customerId, eventId);
    return { answerState: 'yes', markedExpected: r?.markedExpected ?? { name: 'you', at: new Date().toISOString() } };
  }
  const r = await api.openWatchCase(customerId, eventId);
  return { caseId: r?.caseId ?? 'opened' };
}

/** B7: why a My account event reached nobody as the person it is about, in words; null when there is nothing to say. */
export const SELF_UNREACHED_TEXT = Object.freeze({
  'self-off': 'The person this event is about was not told, because the My account audience is off in Rules and audiences. onGuard does not tell anyone else in their place.',
  'no-subject': 'Microsoft Entra ID did not name the person this event is about, so there was nobody to tell.',
  'lookup-failed': 'onGuard could not find the person this event is about in Microsoft Entra ID when it read the event, so they were not told.',
  guest: 'The person this event is about is a guest account, and Watch does not send My account notices to guests.',
  'no-address': 'The person this event is about has no usable email address in Microsoft Entra ID, so they could not be told.',
});

export function noRecipientText(event) {
  if (event?.severity === 'info') return 'Informational events are recorded here but never sent, so no one was notified.';
  return SELF_UNREACHED_TEXT[event?.selfUnreached] ?? null;
}
